import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { checkTokenAuth } from 'shared/lib/authUtils';

const SessionWarningModal = ({ activityTracker }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(5 * 60); // 5 minutes
  const [modalInteractionPaused, setModalInteractionPaused] = useState(false);
  const warningShownRef = useRef(false);

  const isAuthenticated = () => {
    return !!checkTokenAuth();
  };

  useEffect(() => {
    // Only run if user is authenticated and activity tracker exists
    if (!isAuthenticated() || !activityTracker) {
      setShowWarning(false);
      warningShownRef.current = false;
      return;
    }

    const interval = setInterval(() => {
      // Double check authentication
      if (!isAuthenticated()) {
        setShowWarning(false);
        warningShownRef.current = false;
        return;
      }

      const lastActivity = activityTracker.getLastActivity();
      const timeSinceLastActivity = Date.now() - lastActivity;
      const warningTime = 175 * 60 * 1000; // Show warning at 175 minutes
      const logoutTime = 3 * 60 * 60 * 1000; // Auto logout at 3 hours

      if (
        timeSinceLastActivity >= warningTime &&
        timeSinceLastActivity < logoutTime
      ) {
        if (!warningShownRef.current) {
          setShowWarning(true);
          setModalInteractionPaused(true);
          warningShownRef.current = true;

          // Temporarily pause activity tracking to prevent modal from closing
          if (activityTracker.pauseTracking) {
            activityTracker.pauseTracking();
          }
        }

        const remaining = Math.ceil(
          (logoutTime - timeSinceLastActivity) / 1000
        );
        setCountdown(remaining);
      } else if (timeSinceLastActivity >= logoutTime) {
        // PENTING: Hanya force logout jika warning sudah ditampilkan
        if (warningShownRef.current) {
          console.log('Session timeout - forcing logout after warning shown');
          setShowWarning(false);
          warningShownRef.current = false;
          activityTracker.forceLogout();
        }
      } else {
        // Reset warning jika user aktif kembali
        if (warningShownRef.current && timeSinceLastActivity < warningTime) {
          console.log('User became active again - hiding warning');
          setShowWarning(false);
          setModalInteractionPaused(false);
          warningShownRef.current = false;

          if (activityTracker.resumeTracking) {
            activityTracker.resumeTracking();
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activityTracker, modalInteractionPaused]);

  const handleStayLoggedIn = () => {
    if (!isAuthenticated()) return;

    // Resume activity tracking and reset timer
    if (activityTracker.resumeTracking) {
      activityTracker.resumeTracking();
    }

    activityTracker.resetTimer();
    setShowWarning(false);
    setModalInteractionPaused(false);
    warningShownRef.current = false;
  };

  const handleLogout = () => {
    if (!isAuthenticated()) return;

    setModalInteractionPaused(false);
    warningShownRef.current = false;
    activityTracker.forceLogout();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Prevent modal from closing
  const handleCancel = (e) => {
    // Prevent modal from closing when clicking outside or pressing ESC
    e?.preventDefault();
    e?.stopPropagation();
    return false;
  };

  // Don't render modal if user is not authenticated
  if (!isAuthenticated() || !activityTracker) {
    return null;
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleOutlined className="text-xl text-yellow-600" />
          <span className="font-semibold text-yellow-700">
            Session Expiring Soon
          </span>
        </div>
      }
      open={showWarning}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={400}
      onCancel={handleCancel} // Override cancel behavior
      destroyOnClose={false}
      footer={[
        <Button key="logout" onClick={handleLogout}>
          Logout Now
        </Button>,
        <Button key="stay" type="primary" onClick={handleStayLoggedIn}>
          Stay Logged In
        </Button>,
      ]}
      style={{
        pointerEvents: 'auto',
      }}
      wrapClassName="session-warning-modal-wrapper"
    >
      <div className="py-4">
        <div className="text-center mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
          <div className="text-3xl font-bold mb-2 text-yellow-600">
            {formatTime(countdown)}
          </div>
          <div className="text-sm text-gray-600">Time remaining</div>
        </div>

        <div className="text-center text-gray-700">
          <p className="mb-2">Your session will expire due to inactivity.</p>
          <p className="text-sm">
            Click <strong>Stay Logged In</strong> to continue your session.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default SessionWarningModal;
